const { useEffect } = require("react")
const { debounce } = require("throttle-debounce")

const WP_API_BASE = "https://www.dekoder.org/wp-json/wp/v2";
const DELAY_BETWEEN_CHECKS = 300; // ms

// Mapping: slug prefix -> WP API type
const TYPE_MAP = [
  { prefix: "gnose/", type: "gnoses" },
  { prefix: "gnoses/", type: "gnoses" },
  { prefix: "pages/", type: "pages" },
  { prefix: "article/", type: "posts" },
  { prefix: "posts/", type: "posts" },
];

// Term ID that marks a gnose as a "blurb" format in WP
const BLURB_FORMAT_ID = 3517;

function getTypeFromAlias(alias) {
  if (!alias) return "posts";

  // strip optional leading language code like "de/" or "ru/"
  const cleaned = alias.replace(/^\/+/, ""); // remove any leading slashes
  const withoutLang = /^[a-z]{2}\//i.test(cleaned) ? cleaned.replace(/^[a-z]{2}\//i, "") : cleaned;

  for (const { prefix, type } of TYPE_MAP) {
    if (withoutLang.startsWith(prefix) || cleaned.startsWith(prefix)) return type;
  }
  // Default: posts
  return "posts";
}

// Safely extract a plain string from WP fields which can be either a string
// or an object like { rendered: "..." }. Always return a string (may be "").
function renderedString(field) {
  if (!field && field !== "") return "";
  if (typeof field === "string") return field;
  if (field && typeof field === "object") {
    if (typeof field.rendered === "string") return field.rendered;
    // some fields may be arrays or other shapes — try to stringify safely
    try {
      const s = String(field);
      return s === "[object Object]" ? "" : s;
    } catch (e) {
      return "";
    }
  }
  return "";
}

class PopupAggregatorWP {
  constructor(preloadedPopups = []) {
    this.popups = [];
    this.debouncedBulkCheck = debounce(1000, this.bulkCheck.bind(this));
    this.addPreloaded(preloadedPopups);
  }

  get uncheckedPopups() {
    return this.popups.filter((p) => !p.loaded);
  }

  get checkedPopups() {
    return this.popups.filter((p) => p.loaded);
  }

  getPopupByUrl(url) {
    return this.getPopupByAlias(aliasFromUrl(url));
  }

  getPopupByAlias(alias) {
    return this.popups.find((p) => p.alias === alias);
  }

  addByUrl(url) {
    return this.addByAlias(aliasFromUrl(url));
  }

  addByAlias(alias) {
    return this.getPopupByAlias(alias) || new PopupWP({ alias }, this).promise;
  }

  addPreloaded(preloadedPopups = []) {
    preloadedPopups.forEach((p) => {
      if (!this.getPopupByAlias(p.alias)) new PopupWP(p, this);
    });
  }

  async bulkCheck(debug = false) {
    if (!this.uncheckedPopups.length) return this.checkedPopups;
    const aliases = this.uncheckedPopups.map((p) => p.alias);

    for (const alias of aliases) {
      const type = getTypeFromAlias(alias);
  // Extract the last non-empty path segment as slug. This handles
  // trailing slashes (e.g. "en/gnose/slug/" -> "slug") and empty alias.
  const slug = (alias || "").split("/").filter(Boolean).pop() || "";
      const endpoint = `${WP_API_BASE}/${type}?slug=${slug}`;
      if (debug) console.log("WP API Query:", endpoint);
      try {
        const response = await fetch(endpoint);
        const items = await response.json();
        if (items && items.length > 0) {
          const item = items[0];
          const popup = this.getPopupByAlias(alias);
          if (popup) {
            // Only use excerpt for popup text and ensure it's always a string
            const text = renderedString(item.excerpt);

            // determine output type: map WP type -> internal, but allow format overrides
            let resolvedType = type == "gnoses" ? "gnose" : type;
            // Read gnose-formats safely and check numeric/string IDs.
            const gnoseFormatsRaw = item["gnose-formats"] || item.gnose_formats || item.gnoseFormats || [];
            const gnoseFormatIds = Array.isArray(gnoseFormatsRaw)
              ? gnoseFormatsRaw.map((v) => (typeof v === "string" ? Number(v) : v)).filter((n) => !Number.isNaN(n))
              : [];
            if (gnoseFormatIds.includes(BLURB_FORMAT_ID)) {
              resolvedType = "blurb";
            } else if (Array.isArray(item.class_list)) {
              // fallback: check class_list for the blurb marker
              if (item.class_list.some((c) => /gnose-format-blurb|dekoder_gnose-format-blurb/.test(c))) {
                resolvedType = "blurb";
              }
            }

            popup.setPopup({
              type: resolvedType,
              title: renderedString(item.title),
              text,
              loaded: true,
            });
          }
        }
      } catch (e) {
        if (debug) console.error("WP API Error for", alias, e);
      }
      await new Promise(res => setTimeout(res, DELAY_BETWEEN_CHECKS));
    }
    return this.checkedPopups;
  }
}

class PopupWP {
  constructor(props, aggregator) {
    this.alias = props.alias || aliasFromUrl(props.url);
    this.type = props.type || "";
    this.title = props.title || "";
    this.text = props.text || "";
    this.loaded = props.loaded || false;
    aggregator.popups.push(this);
    if (typeof window !== "undefined") {
      this.aggregator = aggregator;
      if (!this.loaded) this.aggregator.debouncedBulkCheck();
    }
  }

  get promise() {
    if (typeof window === "undefined") return null;
    const self = this;
    return new Promise((resolve) => (self.promiseResolver = resolve));
  }

  setPopup({ type, title, text, loaded }) {
    this.type = type;
    this.title = title;
    this.text = text;
    this.loaded = loaded;
    if (this.promiseResolver) this.promiseResolver(this);
  }
}

function aliasFromUrl(url = "") {
  // Example: https://your-wp-site.com/gnoses/moskau-das-dritte-rom
  const rx = /https?:\/\/[^\/]+\/(.*)/;
  const match = url.match(rx);
  return match ? match[1] : "";
}

const popupAggregatorWP = new PopupAggregatorWP();

function usePreloadedPopupsWP(popups = "[]") {
  useEffect(() => {
    const parsedPopups = JSON.parse(popups).filter((p) => typeof p === "object");
    popupAggregatorWP.addPreloaded(parsedPopups);
  }, [popups]);
}

exports.PopupAggregatorWP = PopupAggregatorWP;
exports.popupAggregatorWP = popupAggregatorWP;
exports.usePreloadedPopupsWP = usePreloadedPopupsWP;
